/**
 * OperationsSops.tsx — Operations / SOPs (M1, the Operations wedge module).
 *
 * The mockup's SOPs view (klaxiora-crm-mockup-full.html :1923-1936): a card
 * grid of procedures — title, "Category · Owner · Updated" meta, steps
 * preview (:5001-5013) — with a New SOP affordance and click-to-edit
 * carrying Delete (:3050-3087, :4984-4989). Backed for real by
 * /v1/operations/sops (0099); owners are employees (decision M0), picked
 * from the live active-employee list. The create/edit form is the house
 * right-side drawer (the NewContactDrawer precedent), not the mockup's
 * centered modal, and the queue re-fetches after every write — the server
 * row is authoritative (the SalesLeads reloadKey pattern).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchSops, createSop, updateSop, deleteSop, fetchDirectory } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { overlay } from '../../../../theme/tokens'
import type { OpsSop } from '../../types'
import { SOP_CATEGORIES, sopMeta, sopCountLine } from './sopsView'
import { useWriteAccess } from '../../../../lib/writeAccess'

// ── Create / Edit drawer ──────────────────────────────────────────────────────

function SopDrawer({ sop, onClose, onSaved }: {
  sop: OpsSop | null // null = create
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = useState(sop?.title ?? '')
  const [category, setCategory] = useState(sop?.category ?? 'onboarding')
  const [body, setBody] = useState(sop?.body ?? '')
  const [assignedTo, setAssignedTo] = useState(sop?.assignedTo ?? '')
  // SOP create / update / delete are records-level writes (lib/writeAccess):
  // without access the drawer is a reader — fields take no edits, no Save or
  // Delete.
  const { records } = useWriteAccess()

  // Owner picklist = live ACTIVE employees (the mockup's activeEmployeeIds
  // pool, :4898-4904, realized per M0 as the real employees table).
  const employees = useAsync(() => fetchDirectory({ status: 'active' }), [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleSave() {
    if (!title.trim()) { toast.error('Title is required'); return }
    const write = { title: title.trim(), category, body, assignedTo: assignedTo || undefined }
    try {
      if (sop) {
        await updateSop(sop.id, write)
        toast.success('SOP updated')
      } else {
        await createSop(write)
        toast.success('SOP created')
      }
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, sop ? 'Failed to update SOP' : 'Failed to create SOP'))
    }
  }

  async function handleDelete() {
    if (!sop) return
    try {
      await deleteSop(sop.id)
      toast.success('SOP deleted')
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to delete SOP'))
    }
  }

  const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-muted transition-all'
  const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="fixed top-0 right-0 bg-surface-card border-l border-edge-row flex flex-col"
        style={{ height: '100dvh', width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{sop ? (records ? 'Edit SOP' : sop.title) : 'New SOP'}</div>
            {records && <div className="text-12 text-ink-muted mt-1">Document a procedure so it's consistent no matter who's doing it.</div>}
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>Title<span className="text-danger ml-1">*</span></label>
            <input type="text" value={title} readOnly={!records} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Client Onboarding Checklist" autoFocus className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Category</label>
              <select value={category} onChange={e => { if (records) setCategory(e.target.value) }} className={inputCls}>
                {SOP_CATEGORIES.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Owner</label>
              {employees.error ? (
                <div className="text-12 text-ink-muted px-1 py-3">Couldn't load employees</div>
              ) : (
                <select value={assignedTo} onChange={e => { if (records) setAssignedTo(e.target.value) }}
                  disabled={employees.loading} className={inputCls}>
                  <option value="">Unassigned</option>
                  {(employees.data ?? []).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls}>Steps</label>
            <textarea value={body} readOnly={!records} onChange={e => setBody(e.target.value)}
              placeholder={'1. Do this...\n2. Then this...'} rows={8}
              className={`${inputCls} resize-none`} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          {sop && records ? (
            <button onClick={handleDelete}
              className="k-btn-secondary text-danger">
              Delete
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="k-btn-secondary">
              Cancel
            </button>
            {records && (
              <button onClick={handleSave}
                className="k-btn">
                {sop ? 'Save Changes' : 'Save SOP'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function OperationsSops() {
  const [reloadKey, setReloadKey] = useState(0)
  const [drawer, setDrawer] = useState<{ open: boolean; sop: OpsSop | null }>({ open: false, sop: null })
  const { data, loading, error } = useAsync(fetchSops, [reloadKey])
  // New SOP is a records-level write (lib/writeAccess; fail closed). A card
  // still opens its SOP — read-only without access.
  const { records } = useWriteAccess()

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const sops = data ?? []

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="SOPs"
        subtitle={sops.length === 0 ? 'Documented procedures for how work gets done.' : sopCountLine(sops.length)}
        action={records ? (
          <button className="k-btn inline-flex items-center gap-2" onClick={() => setDrawer({ open: true, sop: null })}>
            <Plus size={14} /> New SOP
          </button>
        ) : undefined}
      />

      {sops.length === 0 ? (
        <div className="closr-card">
          <EmptyState
            message="No SOPs yet"
            hint={records ? 'Document a procedure and it will appear here' : 'Procedures will appear here once they are documented'}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {sops.map(sop => (
            <button key={sop.id} onClick={() => setDrawer({ open: true, sop })}
              className="closr-card px-5 py-4 text-left cursor-pointer hover:bg-surface-row-hover transition-colors">
              <div className="text-13 font-semibold text-ink-primary">{sop.title}</div>
              <div className="text-11 text-ink-muted mt-1">{sopMeta(sop)}</div>
              {sop.body && (
                <div className="text-12 text-ink-secondary mt-3 whitespace-pre-line overflow-hidden"
                  style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
                  {sop.body}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {drawer.open && (records || drawer.sop) && (
        <SopDrawer
          sop={drawer.sop}
          onClose={() => setDrawer({ open: false, sop: null })}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
    </div>
  )
}
